import React from 'react';
import { useIntl, history } from 'umi';
import classnames from 'classnames';
import WechatGamesTop from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/WechatGamesTop';
import DouyinGamesTop from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/components/DouyinGamesTop';
import ReuseTabPageTemplate from '@intelligence/v3/components/ReuseTabPageTemplate';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import useUrlSyncActiveId from '@intelligence/v3/_hooks/useUrlSyncActiveId';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

import './index.scss';

import WeixinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import DouYinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-dy.png';

type ICasualMiniGameRankingsProps = IBaseProps;
type TTabId = 'wechatgames' | 'douyingames';

interface ITabConfig {
  id: TTabId;
  label: React.ReactNode;
  pageLogKey: string;
  downloadLogKey: string;
  condKey?: string;
}

const TAB_IDS: TTabId[] = ['wechatgames', 'douyingames'];
const DEFAULT_ACTIVE_TAB_ID: TTabId = 'wechatgames';
const PAGE_PATH_PREFIX = '/intelligence/casualGamesHub/casualMiniGameRankings';

const CasualMiniGameRankings: React.FC<ICasualMiniGameRankingsProps> = (props) => {
  const { style, className } = props;

  const query = history.location.query || {};

  const { formatMessage } = useIntl();

  const [activeId, setActiveId] = useUrlSyncActiveId(TAB_IDS, DEFAULT_ACTIVE_TAB_ID);

  const tabCondKeysFromBackend = React.useMemo(() => {
    try {
      const encoded = query.tab_cond_keys as string;
      if (!encoded) {
        return {};
      }
      return JSON.parse(decodeURIComponent(encoded));
    } catch (error) {
      console.error('[Tab Cond Keys] Parse error:', error);
      return {};
    }
  }, [query.tab_cond_keys]);

  const tabConfigs = React.useMemo(() => {
    const configs: Record<TTabId, ITabConfig> = {
      wechatgames: {
        id: 'wechatgames',
        label: (
          <div className="dbi-tab-title">
            <img className="dbi-tab-title-icon-new" src={WeixinIcon} alt="" />
            <span className="dbi-tab-title-text">{formatMessage({ id: `dbi_store_rank_wechat_games` })}</span>
          </div>
        ),
        pageLogKey: 'casualGamesHubMiniGameRankings',
        downloadLogKey: 'casualGamesHubMiniGameRankingsDownloadClick',
      },
      douyingames: {
        id: 'douyingames',
        label: (
          <div className="dbi-tab-title">
            <img className="dbi-tab-title-icon-new" src={DouYinIcon} alt="" />
            <span className="dbi-tab-title-text">{formatMessage({ id: `dbi_store_rank_douyin_games` })}</span>
          </div>
        ),
        pageLogKey: 'casualGamesHubMiniGameRankingsDouyin',
        downloadLogKey: 'casualGamesHubDouyinGameRankingsDownloadClick',
      },
    };

    Object.keys(configs).forEach((tabId) => {
      const backendCondKey = (tabCondKeysFromBackend as Record<string, string>)[tabId];
      if (backendCondKey) {
        configs[tabId as keyof typeof configs].condKey = backendCondKey;
      }
    });

    return configs;
  }, [formatMessage, tabCondKeysFromBackend]);

  // 页面埋点
  usePageLog(tabConfigs[activeId as TTabId]?.pageLogKey);

  /**
   * Tab 切换处理函数
   * 更新激活状态并同步 URL 参数，触发筛选器 API 获取对应配置
   */
  const handleTabChange = (tab: { id: string }): void => {
    const tabId = (tab.id as TTabId) || DEFAULT_ACTIVE_TAB_ID;
    if (tabId === activeId) {
      return;
    }

    setActiveId(tabId);
    const currentQuery = history.location.query || {};
    const condKey = tabConfigs[tabId]?.condKey;

    history.push({
      pathname: `${PAGE_PATH_PREFIX}/${tabId}`,
      query: {
        ...currentQuery,
        ...(condKey ? { filter_tag_name: condKey } : {}),
      },
    });
  };

  const tabs = React.useMemo(
    () =>
      Object.values(tabConfigs).map((config) => ({
        id: config.id,
        label: config.label,
        component:
          config.id === 'wechatgames' ? (
            <WechatGamesTop
              pageLogConfigKey={config.pageLogKey}
              downloadLogConfigKey={config.downloadLogKey}
              condKey={config.condKey}
            />
          ) : config.id === 'douyingames' ? (
            <DouyinGamesTop
              pageLogConfigKey={config.pageLogKey}
              downloadLogConfigKey={config.downloadLogKey}
              condKey={config.condKey}
            />
          ) : null,
      })),
    [tabConfigs],
  );

  const activeTab = tabs.find((item) => item.id === activeId) || tabs[0];

  return (
    <ReuseTabPageTemplate
      style={style}
      className={classnames('dbi-reuse-page-casual-games-store-ranks', className)}
      title={formatMessage({ id: 'dbi_reuse_page_title_mini_game_rankings' })}
      pageLogKeyMap={{
        wechatgames: tabConfigs.wechatgames.pageLogKey,
        douyingames: tabConfigs.douyingames.pageLogKey,
      }}
      tabs={tabs}
      activeId={activeId}
      onActive={handleTabChange}
    >
      <MarqueeBanner />
      {activeTab?.component}
    </ReuseTabPageTemplate>
  );
};

export default CasualMiniGameRankings;
